import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Search,
  CheckCircle2,
  Circle,
  Calendar,
  MoreHorizontal,
  AlertTriangle,
  ListTodo,
  PlayCircle,
  XCircle,
} from 'lucide-react';
import { Card } from '../components/Card';
import { Badge, getStatusVariant } from '../components/Badge';
import { Button, IconButton } from '../components/Button';
import { StatCard } from '../components/StatCard';
import { tasksApi } from '../lib/api';
import { format, isToday, isTomorrow, isPast, parseISO } from 'date-fns';
import type { Task, TaskStatus, TaskPriority } from '../types';

const STATUS_OPTIONS: TaskStatus[] = ['pending', 'in_progress', 'completed', 'cancelled'];
const PRIORITY_OPTIONS: TaskPriority[] = ['low', 'medium', 'high', 'urgent'];

function getTaskIcon(status: TaskStatus) {
  switch (status) {
    case 'completed':
      return <CheckCircle2 className="w-5 h-5 text-[var(--asi-success)]" />;
    case 'in_progress':
      return <PlayCircle className="w-5 h-5 text-[var(--asi-info)]" />;
    case 'cancelled':
      return <XCircle className="w-5 h-5 text-[var(--asi-text-muted)]" />;
    default:
      return <Circle className="w-5 h-5 text-[var(--asi-text-muted)]" />;
  }
}

function getDueDateLabel(dueDate: string | null) {
  if (!dueDate) return null;

  const date = parseISO(dueDate);
  if (isToday(date)) return { label: 'Today', variant: 'warning' as const };
  if (isTomorrow(date)) return { label: 'Tomorrow', variant: 'info' as const };
  if (isPast(date)) return { label: 'Overdue', variant: 'error' as const };
  return { label: format(date, 'MMM d'), variant: 'default' as const };
}

export function Tasks() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['tasks', statusFilter, priorityFilter],
    queryFn: () =>
      tasksApi.list({
        status: statusFilter || undefined,
        priority: priorityFilter || undefined,
        limit: 100,
      }),
  });

  const updateTaskMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Task> }) =>
      tasksApi.update(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });

  const tasks = (data?.tasks || []) as Task[];

  // Filter by search query
  const filteredTasks = tasks.filter((task) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      task.title.toLowerCase().includes(query) ||
      task.description?.toLowerCase().includes(query)
    );
  });

  // Group tasks by status
  const tasksByStatus = {
    pending: filteredTasks.filter((t) => t.status === 'pending'),
    in_progress: filteredTasks.filter((t) => t.status === 'in_progress'),
    completed: filteredTasks.filter((t) => t.status === 'completed'),
  };

  const overdueCount = filteredTasks.filter(
    (t) => t.due_date && isPast(parseISO(t.due_date)) && t.status !== 'completed'
  ).length;

  const toggleComplete = (task: Task) => {
    const newStatus = task.status === 'completed' ? 'pending' : 'completed';
    updateTaskMutation.mutate({ id: task.id, updates: { status: newStatus } });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-in-up">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--asi-text)] tracking-tight">
            Tasks
          </h1>
          <p className="text-[var(--asi-text-secondary)] mt-1">
            Manage your tasks and to-dos
          </p>
        </div>
        <Button icon={<Plus className="w-4 h-4" />}>Add Task</Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="animate-fade-in-up delay-75">
          <StatCard
            title="Pending"
            value={tasksByStatus.pending.length}
            icon={<ListTodo className="w-5 h-5" />}
            variant="accent"
          />
        </div>
        <div className="animate-fade-in-up delay-150">
          <StatCard
            title="In Progress"
            value={tasksByStatus.in_progress.length}
            icon={<PlayCircle className="w-5 h-5" />}
          />
        </div>
        <div className="animate-fade-in-up delay-225">
          <StatCard
            title="Completed"
            value={tasksByStatus.completed.length}
            icon={<CheckCircle2 className="w-5 h-5" />}
          />
        </div>
        <div className="animate-fade-in-up delay-300">
          <StatCard
            title="Overdue"
            value={overdueCount}
            icon={<AlertTriangle className="w-5 h-5" />}
          />
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 animate-fade-in-up">
        <div className="relative flex-1 min-w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--asi-text-muted)]" />
          <input
            type="text"
            placeholder="Search tasks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-[var(--asi-bg)] border border-[var(--asi-bg-tertiary)] rounded-xl text-[var(--asi-text)] placeholder:text-[var(--asi-text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--asi-accent)] focus:border-transparent transition-all"
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2.5 bg-[var(--asi-bg)] border border-[var(--asi-bg-tertiary)] rounded-xl text-[var(--asi-text)] focus:outline-none focus:ring-2 focus:ring-[var(--asi-accent)] cursor-pointer transition-all"
        >
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map((status) => (
            <option key={status} value={status}>
              {status.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
            </option>
          ))}
        </select>

        <select
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value)}
          className="px-4 py-2.5 bg-[var(--asi-bg)] border border-[var(--asi-bg-tertiary)] rounded-xl text-[var(--asi-text)] focus:outline-none focus:ring-2 focus:ring-[var(--asi-accent)] cursor-pointer transition-all"
        >
          <option value="">All Priorities</option>
          {PRIORITY_OPTIONS.map((priority) => (
            <option key={priority} value={priority}>
              {priority.charAt(0).toUpperCase() + priority.slice(1)}
            </option>
          ))}
        </select>
      </div>

      {/* Task List */}
      <Card className="animate-fade-in-up overflow-hidden">
        <div className="divide-y divide-[var(--asi-bg-tertiary)]">
          {isLoading ? (
            <div className="py-12 flex items-center justify-center">
              <div className="w-6 h-6 border-2 border-[var(--asi-accent)] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredTasks.length === 0 ? (
            <div className="py-12 text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-[var(--asi-bg-tertiary)] flex items-center justify-center">
                <ListTodo className="w-6 h-6 text-[var(--asi-text-muted)]" />
              </div>
              <p className="text-[var(--asi-text-secondary)]">No tasks found</p>
              <p className="text-sm text-[var(--asi-text-muted)] mt-1">
                Create your first task to get started
              </p>
            </div>
          ) : (
            filteredTasks.map((task, idx) => {
              const dueDateInfo = getDueDateLabel(task.due_date);

              return (
                <div
                  key={task.id}
                  className="p-4 flex items-center gap-4 hover:bg-[var(--asi-bg-secondary)] transition-colors animate-slide-in"
                  style={{ animationDelay: `${idx * 30}ms` }}
                >
                  <button
                    onClick={() => toggleComplete(task)}
                    className="flex-shrink-0 p-1 rounded-lg hover:bg-[var(--asi-bg-tertiary)] transition-colors"
                  >
                    {getTaskIcon(task.status)}
                  </button>

                  <div className="flex-1 min-w-0">
                    <p
                      className={`font-medium ${
                        task.status === 'completed'
                          ? 'line-through text-[var(--asi-text-muted)]'
                          : 'text-[var(--asi-text)]'
                      }`}
                    >
                      {task.title}
                    </p>
                    {task.description && (
                      <p className="text-sm text-[var(--asi-text-muted)] truncate">
                        {task.description}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-3 flex-shrink-0">
                    {dueDateInfo && (
                      <div className="flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5 text-[var(--asi-text-muted)]" />
                        <Badge variant={dueDateInfo.variant} size="sm">
                          {dueDateInfo.label}
                        </Badge>
                      </div>
                    )}

                    <Badge variant={getStatusVariant(task.priority)} size="sm">
                      {task.priority}
                    </Badge>

                    <select
                      value={task.status}
                      onChange={(e) =>
                        updateTaskMutation.mutate({
                          id: task.id,
                          updates: { status: e.target.value as TaskStatus },
                        })
                      }
                      className="px-2.5 py-1 text-xs bg-[var(--asi-bg)] border border-[var(--asi-bg-tertiary)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--asi-accent)] cursor-pointer"
                    >
                      {STATUS_OPTIONS.map((status) => (
                        <option key={status} value={status}>
                          {status.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                        </option>
                      ))}
                    </select>

                    <IconButton
                      icon={<MoreHorizontal className="w-4 h-4" />}
                      label="More options"
                      size="sm"
                    />
                  </div>
                </div>
              );
            })
          )}
        </div>
      </Card>
    </div>
  );
}
